import { useState, useReducer, useEffect, useCallback } from 'react';
import { usePortalUserManagementService } from '../services/portalUserManagementService';
import { useSnackbar } from '../../../shared/hooks/useSnackbar';
import { RpcError } from '../../../shared/api/rpcClient';
import type { CognitoUser } from '../../../types';

interface FormState {
  email: string;
  name: string;
}

type FormAction =
  | { type: 'SET_FIELD'; field: keyof FormState; payload: string }
  | { type: 'RESET' };

const initialFormState: FormState = { email: '', name: '' };

function formReducer(state: FormState, action: FormAction): FormState {
  switch (action.type) {
    case 'SET_FIELD': return { ...state, [action.field]: action.payload };
    case 'RESET': return initialFormState;
  }
}

export type ViewMode = 'list' | 'invite';

export type { FormState, FormAction };

export function usePortalUserForm(isOpen: boolean, customerId: string) {
  const service = usePortalUserManagementService();
  const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();

  const [form, dispatchForm] = useReducer(formReducer, initialFormState);
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [loading, setLoading] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CognitoUser | null>(null);
  const [hasAttemptedInvite, setHasAttemptedInvite] = useState(false);

  const checkAccess = useCallback(async () => {
    setLoading(true);
    setError(null);
    setAccessDenied(false);

    try {
      await service.listPortalUsers(customerId);
    } catch (err: unknown) {
      if (err instanceof RpcError) {
        if (err.code === -32003) {
          setAccessDenied(true);
        } else if (err.message.includes('not configured')) {
          setError('Portal user management is not configured.');
        } else {
          setError(err.message || 'Failed to load portal users');
        }
      } else {
        setError('Failed to load portal users');
      }
    } finally {
      setLoading(false);
    }
  }, [service, customerId]);

  useEffect(() => {
    if (isOpen) {
      checkAccess();
      dispatchForm({ type: 'RESET' });
      setViewMode('list');
      setHasAttemptedInvite(false);
    }
  }, [isOpen, checkAccess]);

  const handleInvite = async (): Promise<boolean> => {
    setHasAttemptedInvite(true);
    if (!form.email.trim() || !form.name.trim()) return false;

    setInviting(true);
    setError(null);

    try {
      const result = await service.invitePortalUser(
        form.email.trim(),
        form.name.trim(),
        customerId,
      );
      dispatchForm({ type: 'RESET' });
      setHasAttemptedInvite(false);
      setViewMode('list');
      showSnackbar(`Invited ${result.email}`, 'success');
      return true;
    } catch (err: unknown) {
      const msg = err instanceof RpcError ? err.message : 'Failed to invite user';
      showSnackbar(msg, 'error');
      return false;
    } finally {
      setInviting(false);
    }
  };

  const handleDelete = async (): Promise<boolean> => {
    if (!deleteTarget) return false;

    try {
      await service.deletePortalUser(deleteTarget.username, customerId);
      showSnackbar(`Deleted ${deleteTarget.email}`, 'success');
      return true;
    } catch (err: unknown) {
      const msg = err instanceof RpcError ? err.message : 'Failed to delete user';
      showSnackbar(msg, 'error');
      return false;
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleResetPassword = async (user: CognitoUser) => {
    try {
      await service.resetPortalUserPassword(user.username, customerId);
      showSnackbar(`Password reset sent to ${user.email}`, 'success');
    } catch (err: unknown) {
      const msg = err instanceof RpcError ? err.message : 'Failed to reset password';
      showSnackbar(msg, 'error');
    }
  };

  const handleResendInvite = async (user: CognitoUser) => {
    try {
      await service.resendPortalInvite(user.email, user.name, customerId);
      showSnackbar(`Invite resent to ${user.email}`, 'success');
    } catch (err: unknown) {
      const msg = err instanceof RpcError ? err.message : 'Failed to resend invite';
      showSnackbar(msg, 'error');
    }
  };

  const emailError = hasAttemptedInvite && !form.email.trim() ? 'Email is required' : undefined;
  const nameError = hasAttemptedInvite && !form.name.trim() ? 'Name is required' : undefined;

  return {
    form,
    dispatchForm,
    viewMode,
    setViewMode,
    loading,
    inviting,
    accessDenied,
    error,
    setError,
    deleteTarget,
    setDeleteTarget,
    snackbar,
    closeSnackbar,
    setHasAttemptedInvite,
    emailError,
    nameError,
    handleInvite,
    handleDelete,
    handleResetPassword,
    handleResendInvite,
  };
}
